// Hardware plan core: SPEC roster item 2. Owns cabinet/faucet/door hardware
// selection, shared by the Node demo and the browser preview.
//
// The key move is that metal-family COORDINATION is generative, not just a
// detector bolted on after the fact: the plan first picks ONE metal family
// for the whole room (M-01), then chooses the best item per needed category
// within that family (F-01, fixture finish inherits the hardware family).
// A recommended plan should therefore already read "clean" in the
// coherence lens; the lens is there to catch what a user does AFTER this,
// not to catch the recommendation itself.
//
// Style steering follows the same 75/25 pattern as paintPlan/floorPlan, but
// there is no LRV axis for metal - it reuses the vibe's WALL temperature
// target as a proxy for whether the room wants warm or cool metals, so one
// style definition (style.ts) drives paint, floor, and metal warmth alike.

import hardwareData from "../../data/seed/hardware.json" with { type: "json" };
import { DEFAULT_STYLE_PULL } from "./types.js";
import type { RoomType, Objective, StyleKey } from "./types.js";
import { STYLES } from "./style.js";

export type MetalFamily = "warm" | "cool" | "neutral";
export type HardwareCategory = "cabinet-pull" | "cabinet-knob" | "faucet" | "door-hardware";

interface SeedHardwareItem {
  brand: string;
  category: HardwareCategory;
  name: string;
  finish: string;
  metal_family: MetalFamily;
  price_each: number;
  durability: string;
  rental_suitability: number;
  channels: string[];
}

const ITEMS = (hardwareData as { items: SeedHardwareItem[] }).items;
const FAMILIES: MetalFamily[] = ["warm", "cool", "neutral"];

// Which hardware categories a room actually needs. Cabinets live in
// kitchens and baths; every room has a door, so door hardware is the
// room-agnostic default elsewhere.
const ROOM_CATEGORIES: Record<RoomType, HardwareCategory[]> = {
  kitchen: ["cabinet-pull", "faucet"],
  bath: ["cabinet-knob", "faucet"],
  bedroom: ["door-hardware"],
  living: ["door-hardware"],
  dining: ["door-hardware"],
  laundry: ["door-hardware"],
  office: ["door-hardware"],
  other: ["door-hardware"],
};

export interface HardwarePlanInput {
  roomType: RoomType;
  objective: Objective;
  styleVibe?: StyleKey;
  stylePull?: number;
}

export interface HardwareItemPick {
  category: HardwareCategory;
  brand: string;
  name: string;
  finish: string;
  metalFamily: MetalFamily;
  price: number;
}

export interface HardwarePlan {
  metalFamily: MetalFamily;
  picks: HardwareItemPick[];
  totalCost: number;
  reasoning: string[];
}

function principleScore(item: SeedHardwareItem, objective: Objective): number {
  const durability = item.rental_suitability;
  const cost = Math.max(0, 1 - item.price_each / 350);
  const weights = objective === "rental"
    ? { dur: 1.0, cost: 0.6 }
    : objective === "flip"
    ? { dur: 0.6, cost: 0.7 }
    : { dur: 0.6, cost: 0.3 };
  // Absolute 0..1 so the vibe blend in planHardware is a true share.
  return (durability * weights.dur + cost * weights.cost) / (weights.dur + weights.cost);
}

// How well a metal family fits a vibe. No LRV axis for metal, so this reuses
// the vibe's WALL temperature target: a vibe that wants warm walls also
// tends to want warm metal (brass, bronze); a vibe that wants cool/neutral
// walls leans cool/steel. Neutral metals (matte black, pewter) read as
// broadly compatible with everything, so they get a flat, decent baseline
// rather than swinging hard either way.
function familyStyleFit(family: MetalFamily, styleKey?: StyleKey): number {
  if (!styleKey) return 0.5;
  const target = STYLES[styleKey]?.wall.temp ?? 0;
  const warmAffinity = Math.max(-1, Math.min(1, target / 0.3));
  if (family === "warm") return (1 + warmAffinity) / 2;
  if (family === "cool") return (1 - warmAffinity) / 2;
  return 0.62; // neutral: safe across nearly every vibe
}

export function planHardware(input: HardwarePlanInput): HardwarePlan {
  const categories = ROOM_CATEGORIES[input.roomType] ?? ["door-hardware"];
  const pull = input.styleVibe ? Math.max(0, Math.min(1, input.stylePull ?? DEFAULT_STYLE_PULL)) : 0;

  // Step 1: score each metal family for this room, by its best available
  // item per needed category (skip categories with no option in that
  // family), blended with the vibe fit at the shared pull weight.
  const familyScores = FAMILIES.map((family) => {
    const bestPerCategory = categories.map((cat) => {
      const pool = ITEMS.filter((i) => i.category === cat && i.metal_family === family);
      if (!pool.length) return null;
      return Math.max(...pool.map((i) => principleScore(i, input.objective)));
    }).filter((s): s is number => s !== null);
    if (!bestPerCategory.length) return { family, score: -1 };
    const principle = bestPerCategory.reduce((s, v) => s + v, 0) / bestPerCategory.length;
    const fit = familyStyleFit(family, input.styleVibe);
    return { family, score: principle * (1 - pull) + fit * pull };
  }).sort((a, b) => b.score - a.score);

  const metalFamily = familyScores[0].family;

  // Step 2: within that family, the best item per needed category.
  const picks: HardwareItemPick[] = categories.map((cat) => {
    const pool = ITEMS.filter((i) => i.category === cat && i.metal_family === metalFamily);
    const chosen = [...pool].sort(
      (a, b) => principleScore(b, input.objective) - principleScore(a, input.objective)
    )[0];
    return {
      category: cat, brand: chosen.brand, name: chosen.name,
      finish: chosen.finish, metalFamily: chosen.metal_family, price: chosen.price_each,
    };
  });

  const totalCost = +picks.reduce((s, p) => s + p.price, 0).toFixed(2);

  const reasoning: string[] = [];
  reasoning.push(
    `${metalFamily[0].toUpperCase()}${metalFamily.slice(1)} metals for this room: ${picks.map((p) => p.finish).join(", ")} all share one family, so nothing here trips M-01.`
  );
  if (categories.includes("faucet") && categories.includes("cabinet-pull")) {
    reasoning.push(`Faucet finish inherits the cabinet hardware family (rule F-01) rather than being chosen in isolation.`);
  }
  if (input.styleVibe) {
    reasoning.push(
      `${STYLES[input.styleVibe]?.label ?? input.styleVibe} nudges metal warmth the same way it nudges wall color, at the same ${Math.round((input.stylePull ?? DEFAULT_STYLE_PULL) * 100)}% pull; principles still picked the family.`
    );
  }
  reasoning.push(`Total hardware for this room: $${totalCost.toFixed(2)} across ${picks.length} piece${picks.length === 1 ? "" : "s"}.`);

  return { metalFamily, picks, totalCost, reasoning };
}
